import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { investorApi, profitLossApi } from '../../services/privateFundApi';
import './InvestorDetail.css';

/**
 * 投资人员详情页面
 */
const InvestorDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [investor, setInvestor] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvestorDetail();
  }, [id]);

  const fetchInvestorDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [investorRes, profitLossRes] = await Promise.all([
        investorApi.getInvestorById(id),
        profitLossApi.getInvestorProfitLoss(id).catch(() => ({ success: false, data: null }))
      ]);

      if (investorRes.success) {
        setInvestor(investorRes.data);
      } else {
        setError(investorRes.message || '获取投资人员详情失败');
      }

      if (profitLossRes.success) {
        setProfitLoss(profitLossRes.data);
      }
    } catch (err) {
      console.error('获取投资人员详情失败:', err);
      setError(err.message || '获取投资人员详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/private-fund');
  };

  const handleProductClick = (productId) => {
    navigate(`/private-fund/products/${productId}`);
  };

  if (loading) {
    return (
      <div className="investor-detail">
        <div className="loading-message">
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="investor-detail">
        <div className="error-message">
          <p>错误: {error}</p>
          <button onClick={fetchInvestorDetail}>重试</button>
        </div>
      </div>
    );
  }

  if (!investor) {
    return (
      <div className="investor-detail">
        <div className="error-message">
          <p>投资人员不存在</p>
          <button onClick={handleBack}>返回</button>
        </div>
      </div>
    );
  }

  return (
    <div className="investor-detail">
      <div className="investor-detail-header">
        <button className="back-button" onClick={handleBack}>
          ← 返回
        </button>
        <h1 className="investor-detail-title">{investor.investorName}的投资详情</h1>
      </div>

      <div className="investor-detail-content">
        {/* 统计信息 */}
        <div className="investor-summary-section">
          <div className="summary-cards">
            <div className="summary-card">
              <h3>总投资金额</h3>
              <p className="summary-value">
                {investor.totalInvestment?.toLocaleString() || 0} 元
              </p>
            </div>
            <div className="summary-card">
              <h3>总盈亏</h3>
              <p className={`summary-value ${investor.totalProfitLoss >= 0 ? 'profit' : 'loss'}`}>
                {investor.totalProfitLoss >= 0 ? '+' : ''}
                {investor.totalProfitLoss?.toLocaleString() || 0} 元
              </p>
            </div>
            <div className="summary-card">
              <h3>盈亏比例</h3>
              <p className={`summary-value ${investor.profitLossRatio >= 0 ? 'profit' : 'loss'}`}>
                {investor.profitLossRatio >= 0 ? '+' : ''}
                {((investor.profitLossRatio || 0) * 100).toFixed(2)}%
              </p>
            </div>
            <div className="summary-card">
              <h3>参与产品数</h3>
              <p className="summary-value">{investor.productCount || 0}</p>
            </div>
          </div>
        </div>

        {/* 产品列表 */}
        {investor.productDetails && investor.productDetails.length > 0 && (
          <div className="investor-products-section">
            <h2 className="section-title">参与的产品</h2>
            <div className="products-list">
              {investor.productDetails.map((product) => (
                <div 
                  key={product.productId}
                  className="product-card"
                  onClick={() => handleProductClick(product.productId)}
                >
                  <div className="product-header">
                    <h3 className="product-name">{product.productName}</h3>
                    <span className={`status-badge ${product.status === 'holding' ? 'holding' : 'redeemed'}`}>
                      {product.status === 'holding' ? '持有' : '已赎回'}
                    </span>
                  </div>
                  <div className="product-info">
                    <div className="info-row">
                      <span className="info-label">申购时间:</span>
                      <span className="info-value">
                        {new Date(product.purchaseTime).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">投资金额:</span>
                      <span className="info-value">
                        {product.investmentAmount?.toLocaleString() || 0} 元
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">投资比例:</span>
                      <span className="info-value">
                        {((product.investmentRatio || 0) * 100).toFixed(2)}%
                      </span>
                    </div>
                    {product.latestNetValue && (
                      <div className="info-row">
                        <span className="info-label">最新净值:</span>
                        <span className="info-value">{product.latestNetValue}</span>
                      </div>
                    )}
                    <div className="info-row">
                      <span className="info-label">盈亏:</span>
                      <span className={`info-value ${product.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                        {product.profitLoss >= 0 ? '+' : ''}
                        {product.profitLoss?.toLocaleString() || 0} 元
                        ({product.profitLossRatio >= 0 ? '+' : ''}
                        {((product.profitLossRatio || 0) * 100).toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 统计信息 */}
        {investor.statistics && (
          <div className="investor-statistics-section">
            <h2 className="section-title">统计信息</h2>
            <div className="statistics-info">
              <div className="stat-item">
                <span className="stat-label">持有产品数:</span>
                <span className="stat-value">{investor.statistics.holdingCount || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">已赎回产品数:</span>
                <span className="stat-value">{investor.statistics.redeemedCount || 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorDetail;























